import { createContext, useContext, useEffect, useMemo, useRef } from "react";

import { useAmpLibraryCanvasEmailSlotsQuery } from "src/graphql";

const CanvasEmailSlotsContext = createContext<ReadonlySet<string>>(new Set());

export const CanvasEmailSlotsProvider = CanvasEmailSlotsContext.Provider;

export function useEmailHasDynamicContent(emailId: string | undefined) {
  const withSlots = useContext(CanvasEmailSlotsContext);
  return emailId !== undefined && withSlots.has(emailId);
}

/** The ids of the canvas's emails that hold dynamic content slots, read in one query and again when an email gets a new version. */
export function useCanvasEmailSlots(
  emails: { emailId: string; versionId: string | null }[],
): ReadonlySet<string> {
  const idsKey = [...new Set(emails.map((email) => email.emailId))]
    .sort()
    .join(",");
  const versionsKey = emails
    .map((email) => `${email.emailId}:${email.versionId}`)
    .sort()
    .join(",");
  const emailIds = useMemo(() => (idsKey ? idsKey.split(",") : []), [idsKey]);
  const { data, refetch } = useAmpLibraryCanvasEmailSlotsQuery(
    { emailIds },
    { enabled: emailIds.length > 0, keepPreviousData: true },
  );

  // A changed set of emails is a new query key and fetches by itself; only a new version of the same emails needs a refetch.
  const seen = useRef({ idsKey, versionsKey });
  useEffect(() => {
    const previous = seen.current;
    seen.current = { idsKey, versionsKey };
    if (previous.idsKey === idsKey && previous.versionsKey !== versionsKey)
      void refetch();
  }, [idsKey, versionsKey, refetch]);

  return useMemo(
    () =>
      new Set(
        (data?.content_emails ?? [])
          .filter(
            (email) =>
              (email.content_dynamic_slot_usages_aggregate.aggregate?.count ??
                0) > 0,
          )
          .map((email) => email.id),
      ),
    [data],
  );
}
